import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { motion } from "framer-motion";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import {
  FiBox,
  FiList,
  FiPlusCircle,
  FiCheckCircle,
  FiTrash2,
  FiEdit,
  FiMap,
  FiGlobe,
  FiHome,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import LayoutSubmasterToUser from "../../components/layoutConfig/layoutSubmasterToUser";

interface Region {
  id: number;
  name: string;
}

interface Unit {
  id: number;
  name: string;
  management_location: Location[];
}

interface Location {
  id: number;
  name: string;
}

interface Company {
  company_id: number;
  name_company: string;
  region_id: number;
  region_name: string;
  unit_id: number;
  unit_name: string;
  location_id: number;
  location_name: string;
}

function CompanyPageSubmaster() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthTokenUserToSubmaster());
  const ApiClient = setupAPIclient(token);

  // Estados principais
  const [data, setData] = useState<Company[]>([]);
  const [pending, setPending] = useState<boolean>(false);
  const [filterText, setFilterText] = useState("");
  const [editId, setEditId] = useState<number | null>(null);

  const [regions, setRegions] = useState<Region[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [form, setForm] = useState({
    name_company: "",
    region_id: "",
    unit_id: "",
    location_id: "",
  });

  const iconColor = "#5dade2";

  // === Buscar empresas ===
  const handleGetCompanies = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post("/management/get/company");
      setData(response.data.success || []);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar empresas");
    } finally {
      setPending(false);
    }
  };

  const handleGetRegions = async () => {
    try {
      const response = await ApiClient.post("/management/get/region");
      setRegions(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleGetUnitsByRegion = async (regionId: number) => {
    try {
      const response = await ApiClient.post("/management/get/association/region", { regionId });
      setUnits(response.data.management_unit || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleGetLocationsByUnit = async (unitId: number) => {
    try {
      const response = await ApiClient.post("/management/get/association/localByUnit", { unitId });
      setLocations(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleGetRegions();
    handleGetCompanies();
  }, []);

  // === Handlers de seleção ===
  const handleRegionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const regionId = e.target.value;
    setForm({ ...form, region_id: regionId, unit_id: "", location_id: "" });
    if (regionId) handleGetUnitsByRegion(Number(regionId));
  };

  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const unitId = e.target.value;
    setForm({ ...form, unit_id: unitId, location_id: "" });
    if (unitId) handleGetLocationsByUnit(Number(unitId));
  };

  const handleLocationChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setForm({ ...form, location_id: e.target.value });
  };

  // === Criar ou atualizar empresa ===
  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();
    const { name_company, region_id, unit_id, location_id } = form;

    if (!name_company || !region_id || !unit_id || !location_id) {
      toast.warning("Preencha todos os campos!");
      return;
    }

    try {
      if (editId) {
        await ApiClient.post("/management/update/company", {
          id: editId,
          ...form,
        });
        toast.success("Empresa atualizada com sucesso!");
      } else {
        await ApiClient.post("/management/create/company", form);
        toast.success("Empresa criada com sucesso!");
      }

      setForm({ name_company: "", region_id: "", unit_id: "", location_id: "" });
      setEditId(null);
      handleGetCompanies();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar empresa");
    }
  };

  // === Editar empresa ===
  const handleEdit = (company: Company) => {
    setEditId(company.company_id);
    setForm({
      name_company: company.name_company,
      region_id: String(company.region_id),
      unit_id: String(company.unit_id),
      location_id: String(company.location_id),
    });
    handleGetUnitsByRegion(company.region_id);
    handleGetLocationsByUnit(company.unit_id);
  };

  // === Excluir empresa ===
  const handleDelete = async (id: number) => {
    toast.info("Excluindo empresa...");
    try {
      await ApiClient.post("/management/delete/company", { id });
      toast.success("Empresa excluída com sucesso!");
      handleGetCompanies();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir empresa");
    }
  };

  // === Filtro ===
  const filteredItems = data.filter((item) =>
    item.name_company.toLowerCase().includes(filterText.toLowerCase())
  );

  // === Colunas da tabela ===
  const columns: TableColumn<Company>[] = [
    { name: "ID", selector: (row) => row.company_id, sortable: true, width: "80px" },
    {
      name: "Empresa",
      selector: (row) => row.name_company,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiBox color={iconColor} /> {row.name_company}
        </div>
      ),
    },
    {
      name: "Região",
      selector: (row) => row.region_name,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiGlobe color={iconColor} /> {row.region_name}
        </div>
      ),
    },
    {
      name: "Unidade",
      selector: (row) => row.unit_name,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiMap color={iconColor} /> {row.unit_name}
        </div>
      ),
    },
    {
      name: "Local",
      selector: (row) => row.location_name,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiHome color={iconColor} /> {row.location_name}
        </div>
      ),
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            style={{background:"#5dade2", color:"white"}}

            className="btn btn-sm btn-outline-primary"
            onClick={() => handleEdit(row)}
          >
            <FiEdit />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row.company_id)}
          >
            <FiTrash2 />
          </motion.button>
        </div>
      ),
    },
  ];

  return (
    <LayoutSubmasterToUser>
      {/* Título e card total */}
      <div className="mb-4">
        <h4 className="d-flex align-items-center gap-2">
          <FiBox color={iconColor} size={26} /> Configuração de Empresa
        </h4>
        <div className="d-flex gap-3 mt-3 flex-wrap">
          <div className="card flex-fill p-3 shadow-sm">
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div>Total de Empresas</div>
                <strong>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <motion.div
        className="card p-4 mb-4 shadow-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <FiPlusCircle color={iconColor} /> {editId ? "Editar Empresa" : "Nova Empresa"}
        </h5>

        <form onSubmit={handleCreateOrUpdate}>
          <div className="row align-items-end g-3">
            <div className="col-md-3">
              <label className="form-label">Nome da Empresa</label>
              <input
                type="text"
                className="form-control"
                placeholder="Digite o nome da empresa"
                value={form.name_company}
                onChange={(e) => setForm({ ...form, name_company: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Região</label>
              <select
                className="form-select"
                value={form.region_id}
                onChange={handleRegionChange}
              >
                <option value="">Selecione...</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Unidade</label>
              <select
                className="form-select"
                value={form.unit_id}
                onChange={handleUnitChange}
                disabled={!units.length}
              >
                <option value="">Selecione...</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Local</label>
              <select
                className="form-select"
                value={form.location_id}
                onChange={handleLocationChange}
                disabled={!locations.length}
              >
                <option value="">Selecione...</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4 d-flex gap-2 mt-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="btn modern-primary-btn d-flex align-items-center justify-content-center gap-1 flex-fill"
              >
                <FiCheckCircle /> {editId ? "Salvar" : "Cadastrar"}
              </motion.button>

              {editId && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setEditId(null);
                    setForm({ name_company: "", region_id: "", unit_id: "", location_id: "" });
                    setUnits([]);
                    setLocations([]);
                  }}
                >
                  <FiX className="me-1" /> Cancelar
                </motion.button>
              )}
            </div>
          </div>
        </form>
      </motion.div>

      {/* Pesquisa */}
      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-white">
            <FiSearch color={iconColor} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Pesquisar empresa..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      {/* Tabela */}
      <DataTable
        columns={columns}
        data={filteredItems}
        progressPending={pending}
        pagination
        highlightOnHover
        pointerOnHover
        responsive
        striped
        dense
        noHeader
      />
    </LayoutSubmasterToUser>
  );
}

export default CompanyPageSubmaster;
